import { motion, AnimatePresence } from 'framer-motion'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import ContentLoader from 'react-content-loader'
import { ISbStoryData } from 'storyblok-js-client'
import { useTheme } from 'styled-components'

import { DynamicBlock } from 'components/blocks/sections/DynamicBlock'
import { Empty } from 'components/blocks/sections/directories/AllEntries/Empty'
import { Button, Icon } from 'components/ui'
import { Link } from 'components/ui/Link'
import { getStory } from 'lib/api'
import { useDataContext } from 'lib/contexts/dataContext'
import { useBodyLock } from 'lib/hooks/useBodyLock'
import useDrawer from 'lib/hooks/useDrawer'
import { useIsMobile } from 'lib/hooks/useIsMobile'
import { useTranslation } from 'lib/i18n'
import {
  PageStoryblok,
  TeamMemberDetailPageStoryblok,
  OpenEveningStoryblok,
  SatelliteClinicPageStoryblok,
} from 'lib/storyblok/types'
import { addAlphaToColor } from 'lib/style'
import { getAnchorFromCmsLink } from 'lib/utils/content'
import { RESOLVE_RELATIONS } from 'pages/[lang]/[[...slug]]'

import { SatelliteClinicPageHeader } from './SatelliteClinicPageHeader'
import { TeamMemberDetailPageHeader } from './TeamMemberDetailPageHeader'

export const PageDrawer = () => {
  const { drawer, close } = useDrawer()
  useBodyLock(!!drawer)

  const { i18n } = useTranslation()
  const theme = useTheme()
  const isMobileDevice = useIsMobile()

  //Floating Button's
  const { floating } = useDataContext()
  const contactPage = floating?.content?.contact_us
  const phones = floating?.content?.phones
  const contactUsBtnLabel = floating?.content?.contact_us_btn

  const router = useRouter()
  const { isPreview } = router

  const [data, setData] = useState<ISbStoryData<
    | PageStoryblok
    | TeamMemberDetailPageStoryblok
    | OpenEveningStoryblok
    | SatelliteClinicPageStoryblok
  > | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(false)

    if (drawer) {
      try {
        const story = (await getStory(drawer, {
          resolve_links: 'url',
          resolve_relations: RESOLVE_RELATIONS.join(','),
          version: isPreview ? 'draft' : 'published',
        })) as ISbStoryData<PageStoryblok>

        setData(story)
      } catch {
        setError(true)
      }
    }

    setLoading(false)
  }, [drawer])

  useEffect(() => {
    if (router.isReady && drawer) {
      fetch()
    }
  }, [fetch, router.isReady, drawer])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        close()
      }
    }

    if (drawer) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [drawer, close])

  const transition = {
    x: { type: 'spring', stiffness: 300, damping: 35 },
    opacity: { duration: 0.3 },
  }

  const paddingDrawer = !isMobileDevice ? 8 : 0

  return (
    <AnimatePresence>
      {drawer && (
        <>
          <motion.div
            onMouseDown={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.3 } }}
            className="fixed top-0 z-[501] w-[100vw] h-full p-2 overflow-hidden bg-foreground-dark/90"
          />
          <motion.div
            initial={{
              x: isMobileDevice ? '0%' : '105%',
              y: isMobileDevice ? '105%' : '0%',
              right: paddingDrawer,
              top: paddingDrawer,
              bottom: paddingDrawer,
              opacity: 1,
            }}
            animate={{
              x: isMobileDevice ? '' : 0,
              y: isMobileDevice ? 0 : '',
              opacity: 1,
            }}
            exit={{
              x: isMobileDevice ? '0%' : '105%',
              y: isMobileDevice ? '105%' : '0%',
              right: 0,
              opacity: 1,
            }}
            transition={transition}
            className="fixed z-[501] w-full h-[calc(100%-3.5rem)] md:w-[calc(100%-1rem)] md:h-full lg:w-auto"
          >
            <div className="flex flex-col relative h-full w-full bg-background md:rounded-5 lg:w-[43.875rem] lg:h-[calc(100%-1rem)]">
              <div className="flex justify-between p-3 lg:pl-8 lg:pr-3 lg:py-3">
                <Link href={drawer} icon={'expand'} variant="drawer">
                  {i18n('expand')}
                </Link>
                <div className="flex items-center gap-2">
                  {floating && (
                    <div className="hidden lg:flex lg:gap-2">
                      {phones && (
                        <Button
                          as="a"
                          href={`tel:${phones[0].phone}`}
                          icon="phone"
                          size="sm"
                        >
                          {phones[0].phone}
                        </Button>
                      )}
                      {contactPage &&
                        (() => {
                          const { href, target, rel } =
                            getAnchorFromCmsLink(contactPage)
                          return (
                            <Button
                              as="a"
                              rel={rel}
                              href={href}
                              target={target}
                              size="sm"
                              variant="secondary"
                            >
                              {contactUsBtnLabel || i18n('floating.contactUs')}
                            </Button>
                          )
                        })()}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={close}
                    className="px-2.5 pt-3 pb-2.5 border border-foreground/10"
                  >
                    <Icon icon="close" />
                  </Button>
                </div>
              </div>
              <div className="flex flex-col overflow-y-scroll pt-3 lg:px-8 lg:pt-5">
                {loading ? (
                  <ContentLoader
                    backgroundColor={addAlphaToColor(
                      theme.colors.foreground.subtle,
                      40
                    )}
                    foregroundColor={addAlphaToColor(
                      theme.colors.foreground.subtle,
                      10
                    )}
                    opacity={0.25}
                    viewBox="0 0 380 190"
                  >
                    <rect
                      x="22"
                      y="0"
                      rx="4"
                      ry="4"
                      width="90%"
                      height="100%"
                    />
                  </ContentLoader>
                ) : error ? (
                  <Empty isError={error} className="py-5 px-8" />
                ) : (
                  <>
                    {data?.content.component === 'team-member-detail-page' && (
                      <TeamMemberDetailPageHeader
                        story={
                          data as ISbStoryData<TeamMemberDetailPageStoryblok>
                        }
                      />
                    )}

                    {data?.content.component === 'satellite-clinic-page' && (
                      <SatelliteClinicPageHeader
                        story={
                          data as ISbStoryData<SatelliteClinicPageStoryblok>
                        }
                      />
                    )}

                    {data?.content?.body?.map((block) => (
                      <DynamicBlock key={block._uid} block={block} />
                    ))}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
